import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Placeholder } from "@/components/Placeholder";
import { BUSINESS, CONTACT, OFFICE_IMAGE, SERVICES } from "@/data/content";

const VALUES = [
  { title: "Honesty", text: "We describe our services accurately and never promise outcomes we cannot control." },
  { title: "Clarity", text: "Written scope, written price, written timeframe — before any work begins." },
  { title: "Craft", text: "Work done properly the first time, tested before it is handed over." },
  { title: "Respect", text: "Your accounts, your data and your decisions stay yours." },
];

const Fact = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1 border-b border-slate-200 py-4 last:border-b-0 sm:flex-row sm:gap-6">
    <dt className="w-44 shrink-0 text-sm font-semibold text-slate-500">{label}</dt>
    <dd data-testid={testId} className="text-sm leading-relaxed text-ink">{children}</dd>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Us" description="Who My Geeks LLC is, what we do, who we serve and how to reach us. Business management and website development services, scoped in writing." />
      <PageHeader overline="About us" title="Who we are and what we do" lede="My Geeks LLC is a B2B consultancy providing custom website architecture, enterprise business management, and marketing strategy to corporate clients. Here is a plain description of who we are and how we work." />

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-12 md:py-24">
        <Reveal className="md:col-span-7">
          <p className="eyebrow">The business</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">What My Geeks LLC does</h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
            <p data-testid="about-b2b">
              {CONTACT.name} is a B2B consultancy providing custom website architecture, enterprise business management, and marketing strategy to corporate clients. We do not offer remote consumer IT troubleshooting, computer repair or technical support. We bill clients individually via custom B2B invoices based on contractual project milestones.
            </p>
            <p data-testid="about-who">
              In practical terms, {CONTACT.name} provides two kinds of service. The first is <strong className="text-ink">website development</strong>: designing, building, redesigning and maintaining business websites and landing pages, including basic SEO setup and contact or lead-capture forms. The second is <strong className="text-ink">business management</strong>: process consulting, operations support, workflow planning, documentation and organisation, digital setup assistance and website/content coordination. We also offer SEO/SMO and digital marketing for businesses that want to be found and remembered online.
            </p>
            <p data-testid="about-serves">
              We serve owner-managed and small-to-mid-sized businesses — professional practices, trades, local shops, consultancies and new ventures — who want one dependable partner for their online presence and their back office. Work is delivered remotely, with meetings by telephone or video call, and we operate from our office in Sheridan, Wyoming.
            </p>
            <p data-testid="about-mission">
              <strong className="text-ink">Our mission</strong> is simple: to help businesses present themselves accurately online and run their operations in an orderly way, through services that are honestly described, clearly scoped and fairly priced.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-5">
          <div className="card overflow-hidden">
            <img src={OFFICE_IMAGE} alt="Professionals at a desk reviewing business documents" className="h-72 w-full object-cover md:h-[26rem]" />
          </div>
        </Reveal>
      </section>

      <section className="border-y border-slate-200 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <p className="eyebrow">Our values</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">What we hold to</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06} className="h-full">
                <div data-testid={`about-value-${i + 1}`} className="card card-hover h-full p-7">
                  <h3 className="font-display text-lg font-semibold tracking-tight">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <p className="eyebrow">Business details</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Who operates this business</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">The details below identify the business behind this website.</p>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-7">
            <dl data-testid="about-business-details" className="card px-6 py-2 md:px-8">
              <Fact label="Trading name" testId="about-trading-name">{CONTACT.name}</Fact>
              <Fact label="Legal name" testId="about-legal-name"><Placeholder field={BUSINESS.legalName} /></Fact>
              <Fact label="Business type" testId="about-business-type"><Placeholder field={BUSINESS.businessType} /></Fact>
              <Fact label="Established" testId="about-established"><Placeholder field={BUSINESS.established} /></Fact>
              <Fact label="EIN" testId="about-ein">{BUSINESS.ein.value}</Fact>
              <Fact label="Registration" testId="about-registration">{BUSINESS.registration}</Fact>
              <Fact label="Client type" testId="about-client-type">Business-to-business (corporate and professional clients)</Fact>
              <Fact label="Billing" testId="about-billing">Custom B2B invoices based on contractual project milestones</Fact>
              <Fact label="Address" testId="about-address">{CONTACT.address}</Fact>
              <Fact label="Country" testId="about-country">{CONTACT.country}</Fact>
              <Fact label="Email" testId="about-email"><a href={`mailto:${CONTACT.email}`} className="font-medium text-brand hover:underline">{CONTACT.email}</a></Fact>
              <Fact label="Phone" testId="about-phone"><a href={CONTACT.phoneHref} className="font-medium text-brand hover:underline">{CONTACT.phone}</a></Fact>
              <Fact label="Business hours" testId="about-hours"><Placeholder field={BUSINESS.hours} /></Fact>
              <Fact label="Services" testId="about-services">
                {SERVICES.map((s, i) => (
                  <span key={s.slug}>
                    <Link to={s.path} className="font-medium text-brand hover:underline">{s.title}</Link>
                    {i < SERVICES.length - 1 ? ", " : ""}
                  </span>
                ))}
              </Fact>
            </dl>
          </Reveal>
        </div>
      </section>

      <CTABand title="Questions about who we are?" text="Write, call or request a quote — we are happy to explain how we work before you commit to anything." />
    </div>
  );
}
